import { IScreenLabelsContext, LabelForm, LabelKey, CustomLabel, defaultPluralMap, defaultSingularMap } from './screen-labels.model';
import { createContext, PropsWithChildren, useState, useEffect, useContext } from 'react';
import ScreenLabelsService from '../../shared/services/screen-labels/screen-labels.service';
import { UserDetailsContext } from '../user-details/user-details';

export const ScreenLabelsContext = createContext<IScreenLabelsContext>({} as IScreenLabelsContext);

function mergeLabels(defaults: Record<LabelKey, LabelForm>, customLabels: CustomLabel[]): Record<LabelKey, LabelForm> {
  const merged = { ...defaults };
  customLabels.forEach(({ gaiaTerm, customTermSingular, customTermPlural }) => {
    if (merged[gaiaTerm]) {
      merged[gaiaTerm] = {
        singular: customTermSingular,
        plural: customTermPlural
      };
    }
  });
  return merged;
}

const buildDefaultLabels = (): Record<LabelKey, LabelForm> =>
  Object.fromEntries(
    Object.entries(defaultSingularMap).map(([key, singular]) => [
      key,
      { singular, plural: defaultPluralMap[singular] || singular + 's' }
    ])
  ) as Record<LabelKey, LabelForm>;

const screenLabelsService = new ScreenLabelsService();
const validKeys: LabelKey[] = ["organisation", "project", "subsidiary", "location", "vehicle", "supplier", "product"];

export const ScreenLabelsProvider = ({ children }: PropsWithChildren) => {
  const [allLabels, setAllLabels] = useState<Record<LabelKey, LabelForm> | undefined>(undefined);
  const userDetailsContext = useContext(UserDetailsContext);

  useEffect(() => {
    const loadCustomLabels = async () => {
      try {
        const custom = await getCustomLabels();
        const merged = mergeLabels(buildDefaultLabels(), custom);
        setAllLabels(merged);
      } catch (error) {
        console.error('Failed to load custom labels:', error);
        setAllLabels(buildDefaultLabels());
      }
    }
    if (userDetailsContext.userDetails) {
      loadCustomLabels();
    }
  }, [userDetailsContext.userDetails]);


  const getCustomLabels = async (): Promise<CustomLabel[]> => {
    const response = await screenLabelsService.getAll();
    return response
      .filter(item => validKeys.includes(item.gaiaTerm as LabelKey))
      .map(item => ({
        gaiaTerm: item.gaiaTerm as LabelKey,
        customTermSingular: item.customTermSingular,
        customTermPlural: item.customTermPlural,
      }));
  };

  const getLabel = (
    key: LabelKey,
    options?: { plural?: boolean; capitalize?: 'first' | 'all' }
  ): string => {
    if (!allLabels) return '';
    let label = options?.plural ? allLabels[key]?.plural : allLabels[key]?.singular;
    if (!label) return '';

    if (options?.capitalize === 'first') {
      return label.charAt(0).toUpperCase() + label.slice(1);
    }
    if (options?.capitalize === 'all') {
      return label.toUpperCase();
    }
    return label;
  };

  const forceUpdateLabels = async () => {
    if (!userDetailsContext.userDetails) return;
    const custom = await getCustomLabels();
    const merged = mergeLabels(buildDefaultLabels(), custom);
    setAllLabels(merged);
  };
  return (
    <ScreenLabelsContext.Provider value={{ getLabel, forceUpdateLabels }}>
      {children}
    </ScreenLabelsContext.Provider>
  );
};
